<template lang="pug">
.relative
  #side.w-full.top-0
    .field.w-full.h-full.flex.items-center.border.px-1.bg-linear-to-b.from-menu-bg-from.to-menu-bg-to.border-menu-bg-border(:class="showArrowBtn ? 'portrait:px-7 portrait:rounded-3xl px-7' : ''")
      client-only
        swiper-container.h-full.w-full(ref="bannerSwiper", init="false", :loop="false", :navigation="false", :pagination="false", lazy="true")
          swiper-slide.swiper-wrapper.w-max.content-center.flex.items-center.py-1(v-for="(nav, index) in sortNavbarList", :key="nav.navbarId", data-location="ad")
            button.nav-item.relative.flex.justify-center.items-center.h-full.rounded-md(type="button", :disabled="nav.disabled", :data-id="nav.navbarId", :class="[checkActive(nav) ? 'bg-linear-to-b from-menu-active-from to-menu-active-to' : '', nav.disabled ? 'disabled-nav' : '']", @click="showPage(nav)")
              .flex.items-center.justify-center.w-max.px-2
                .w-4.h-4.bg-cover.bg-top(:style="nav.iconUrl ? `background-image: url(${nav.iconUrl});` : null", class="me-[0.16rem]")
                span.truncate.text-xs.text-primary-text {{ nav.name }}
        template(v-if="showArrowBtn")
          .swiper-nav-prev.absolute.start-0.inset-y-auto.p-3(class="portrait:start-3", :class="hasPrev ? 'cursor-pointer hover:bg-hover-bg hover:text-hover-on hover:-translate-x-px' : 'opacity-50 cursor-default'", @click="prev")
          .swiper-nav-next.absolute.end-0.inset-y-auto.p-3(class="portrait:end-3", :class="hasNext ? 'cursor-pointer hover:bg-hover-bg hover:text-hover-on hover:-translate-x-px' : 'opacity-50 cursor-default'", @click="next")
</template>

<script setup>
import iconDefault from 'assets/images/side/idxcategory_icon_default_both.png'
import iconDeposit from 'assets/images/side/idxcategory_icon_deposit_normal.png'
import iconFaq from 'assets/images/side/idxcategory_icon_faq_normal.png'
import iconFriends from 'assets/images/side/idxcategory_icon_friends_normal.png'
import iconGame from 'assets/images/side/idxcategory_icon_game_normal.png'
import iconMybet from 'assets/images/side/idxcategory_icon_mybet_normal.png'
import iconPromotion from 'assets/images/side/idxcategory_icon_promotion_normal.png'
import iconTask from 'assets/images/side/idxcategory_icon_task_normal.png'
import iconTransection from 'assets/images/side/idxcategory_icon_transection_normal.png'
import { storeToRefs } from 'pinia'
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance, nextTick, watch } from 'vue'

import { useI18n } from '#imports'
import { useMainStore } from '@/stores/main'
import { isTrue } from '@/utils/siteConfigChecker'

// 圖示映射物件
const iconMap = {
  promotion: iconPromotion,
  transection: iconTransection,
  mybet: iconMybet,
  faq: iconFaq,
  deposit: iconDeposit,
  friends: iconFriends,
  tournament: iconTask,
  promocode: iconPromotion,
  vipcard: iconTask,
}

const props = defineProps({
  menuItemStack: Array,
})

const emit = defineEmits(['play'])

const instance = getCurrentInstance()
const $SoundControl = instance.appContext.config.globalProperties.$SoundControl
const $analytics = instance.appContext.config.globalProperties.$analytics

const { t: $t, locale } = useI18n()

const sideMenu = useSideMenu()
const { setRoom, playGame } = useGame()
const { activityList, navbarSettings } = useActivity('side-sub')
const { verify } = useVerify()
const { checkGameRestriction } = useGameRestriction()

const mainStore = useMainStore()
const { getTrackLocation } = storeToRefs(mainStore)
const { setModal, setModalParams } = mainStore

const { promoCodeEnable } = useSite()

const { isGuest } = useAuthToken()

const swiper = ref(null)
const bannerSwiper = ref(null)
let resizeRaf = null
let initRetryTimer = null
const MAX_INIT_RETRIES = 10
const boundSwipers = new WeakSet()

const canSlidePrev = ref(false)
const canSlideNext = ref(false)
const showArrowBtn = computed(() => hasNext.value || hasPrev.value)
const hasPrev = computed(() => canSlidePrev.value)
const hasNext = computed(() => canSlideNext.value)
const hasPromoCode = computed(() => isTrue(promoCodeEnable.value))
const hasVipCard = computed(() => activityList.value?.find((item) => item.template === 'Template_vip_card_pro'))
const hasTournament = computed(() => activityList.value?.find((item) => item.tournament === 1))
const hasReferralFriends = computed(() => activityList.value?.find((item) => item.template === 'Template_invite_friends'))

const formattedNavbarList = computed(() => {
  return (navbarSettings.value || []).map((element) => {
    let name = element.nameI18n?.[locale.value] ? element.nameI18n[locale.value] : element.nameI18n?.['en-US']
    let iconUrl = element.iconUrl?.[0]?.url
    let display = true
    let disabled = false
    let action = { type: '', target: '', needAuth: true }
    let eventName = null

    switch (element.type) {
      case 'GAME':
        iconUrl = iconUrl || iconGame
        action = { type: 'game', needAuth: true }
        break
      case 'CATEGORY':
        iconUrl = iconUrl || iconGame
        action = { type: 'category', needAuth: false }
        break
      case 'ACTIVITY':
        iconUrl = iconUrl || iconTask
        action = { type: 'modal', target: 'ModalMission', id: element.activityNo, needAuth: false }
        break
      case 'PROMOTIONS':
        iconUrl = iconUrl || iconPromotion
        action = { type: 'modal', target: 'ModalMission', id: element.promotionsNo, needAuth: false }
        break
      case 'ITEM':
        let item = element.item.replace(' ', '').toLowerCase()
        if (item === 'promotions') {
          item = 'promotion'
        }
        if (item === 'transaction') {
          item = 'transection'
        }
        if (item === 'mybets') {
          item = 'mybet'
        }
        iconUrl = iconUrl || iconMap[item] || iconDefault
        switch (element.item) {
          case 'Tournament':
            if (!hasTournament.value) {
              display = false
            }
            const tournamentId = activityList.value?.find((item) => item.classification === 'tournament')?.activityNo
            name = name ? name : $t('common.tournament')
            action = { type: 'modal', target: 'ModalMission', id: tournamentId, needAuth: true }
            eventName = 'Tournament'
            break
          case 'Friends':
            // 當沒有指定活動時 顯示但不可點擊
            disabled = !hasReferralFriends.value
            name = name ? name : $t('common.friends')
            action = { type: 'modal', target: 'ModalFriends', needAuth: true }
            eventName = 'Friends'
            break
          case 'Promo Code':
            if (!hasPromoCode.value) {
              display = false
            }
            name = name ? name : $t('common.promocode')
            action = { type: 'modal', target: 'ModalMission', id: 'promocode', needAuth: true }
            eventName = 'PromoCode'
            break
          case 'VIP Card':
            if (!hasVipCard.value) {
              display = false
            }
            const vipcardId = activityList.value?.find((item) => item.template === 'Template_vip_card')?.activityNo
            action = { type: 'modal', target: `VipCard`, id: vipcardId, needAuth: true }
            name = name ? name : $t('common.vip_card')
            eventName = 'VipCard'
            break
          case 'Promotions':
            const promotionId = activityList.value?.find((item) => item.classification === 'promotion')?.activityNo
            name = name ? name : $t('common.promotion')
            action = { type: 'modal', target: 'ModalMission', id: promotionId, needAuth: false }
            eventName = 'Promotions'
            break
          case 'Deposit':
            name = name ? name : $t('common.deposit')
            action = { type: 'modal', target: 'ModalDepositProxy', needAuth: true }
            eventName = 'Deposit'
            break
          case 'Transaction':
            name = name ? name : $t('common.transaction')
            action = { type: 'modal', target: 'ModalDepositProxy', modal: 'transaction', needAuth: true }
            eventName = 'Transaction'
            break
          case 'My Bets':
            name = name ? name : $t('common.history')
            action = { type: 'modal', target: 'ModalStatement', needAuth: true }
            eventName = 'History'
            break
          case 'FAQ':
            name = name ? name : $t('common.faq')
            action = { type: 'modal', target: 'ModalFAQ', needAuth: false }
            eventName = 'FAQ'
            break
          default:
            action = { type: 'link', target: element.externalLink, needAuth: false }
            eventName = element.item
            break
        }
    }

    return { ...element, display, disabled, name, iconUrl, action, eventName }
  })
})

const sortNavbarList = computed(() => {
  if (!formattedNavbarList.value) return []
  return formattedNavbarList.value
    .filter((nav) => nav.display)
    .sort((a, b) => {
      return a.sort - b.sort
    })
})

const updateNavState = () => {
  const inst = swiper.value
  if (!inst) {
    canSlidePrev.value = false
    canSlideNext.value = false
    return
  }

  const locked = inst.isLocked
  canSlidePrev.value = !locked && !inst.isBeginning
  canSlideNext.value = !locked && !inst.isEnd
}

const bindSwiperEvents = () => {
  const inst = swiper.value
  if (!inst || boundSwipers.has(inst)) return
  boundSwipers.add(inst)
  const events = ['slideChange', 'reachEnd', 'reachBeginning', 'touchEnd', 'transitionEnd', 'update', 'observerUpdate', 'resize']
  events.forEach((evt) => inst.on?.(evt, updateNavState))
}

const initSlider = (retryCount = 0) => {
  const swiperEl = bannerSwiper.value
  const swiperParams = {
    slidesPerView: 'auto',
    centeredSlides: false,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    observeSlideChildren: true,
    pagination: {
      el: '.swiper-pagination',
    },
  }
  if (swiperEl) {
    Object.assign(swiperEl, swiperParams)
    swiperEl.initialize()
    swiper.value = swiperEl.swiper
    bindSwiperEvents()
    updateNavState()
  } else if (retryCount < MAX_INIT_RETRIES) {
    initRetryTimer = setTimeout(() => {
      initRetryTimer = null
      initSlider(retryCount + 1)
    }, 500)
  } else {
    console.warn('[SideSub] Swiper initialization failed after', MAX_INIT_RETRIES, 'retries')
  }
}

const showPage = (item) => {
  $SoundControl.sound.play('click')

  const { action, externalLink } = item
  const { type, target, id, needAuth, modal } = action
  if (needAuth && isGuest.value) {
    setModal('ModalGuestLogin')
  } else {
    if (type === 'modal') {
      if (id) {
        setModalParams({ id })
        setModal(target)
      } else if (modal) {
        setModalParams({ modal })
        setModal(target)
      } else {
        setModal(target)
      }
    } else if (type === 'game') {
      if (checkGameRestriction(item)) {
        return
      }
      play(item)
    } else if (type === 'category') {
      sideMenu.restoreMenuItemStack()
      sideMenu.selectMenuItem(item)
      setRoom(item)
    } else if (type === 'link' && externalLink) {
      const newWindow = window.open()
      newWindow.opener = null
      newWindow.location.href = item.externalLink
    }
  }
}

const checkActive = (item) => {
  return props.menuItemStack?.some((menuItem) => item.categoryId === menuItem.categoryId)
}

const play = async (game) => {
  if (game) {
    $SoundControl.sound.play('click')
    verify('ENTER_GAME').then((isVerified) => {
      if (isVerified) {
        $analytics.push({
          event: 'game_click',
          location: getTrackLocation.value,
          locationId: null,
          provider: game.providerId,
          gameId: game.gameId,
          gameName: game.name['en-US'],
        })
        emit('play')
        nextTick(() => {
          playGame({ game, locale: locale.value })
        })
      }
    })
  }
}

const prev = () => {
  swiper.value?.slidePrev()
}

const next = () => {
  swiper.value?.slideNext()
}

const handleResize = () => {
  if (resizeRaf) cancelAnimationFrame(resizeRaf)
  resizeRaf = requestAnimationFrame(() => {
    resizeRaf = null
    swiper.value?.update?.()
    updateNavState()
  })
}

onMounted(() => {
  initSlider()
  window.addEventListener('resize', handleResize)
})

watch(
  () => sortNavbarList.value?.length,
  async () => {
    await nextTick()
    swiper.value?.update?.()
    updateNavState()
  }
)

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeRaf) {
    cancelAnimationFrame(resizeRaf)
    resizeRaf = null
  }
  if (initRetryTimer) {
    clearTimeout(initRetryTimer)
    initRetryTimer = null
  }
  const inst = swiper.value
  if (inst) {
    const events = ['slideChange', 'reachEnd', 'reachBeginning', 'touchEnd', 'transitionEnd', 'update', 'observerUpdate', 'resize']
    events.forEach((evt) => inst.off?.(evt, updateNavState))
  }
})
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

button {
  outline: none;
  border: none;
}

#side {
  z-index: 5;
  transition: all 0.3s ease;
  height: 2rem;
}

#side .field {
  @apply from-menu-bg-from to-menu-bg-to border-menu-bg-border bg-linear-to-b;
  border-radius: 0.65rem;
  box-shadow: none;
}

.nav-item {
  padding: 4px;
  font-size: 0.75rem;
}

.nav-item button:disabled {
  @apply grayscale;
}

.swiper-wrapper {
  position: relative;
}

.swiper-nav-prev {
  width: 1rem;
  height: 1rem;
  background-image: url('assets/images/list/list_img_forward_normal.png');
  background-size: contain;
  inset-inline-start: 0.25rem;
  background-color: rgba(0, 0, 0, 0);
}

.swiper-nav-next {
  width: 1rem;
  height: 1rem;
  background-image: url('assets/images/list/list_img_forward_normal.png');
  background-size: contain;
  inset-inline-end: 0.25rem;
  background-color: rgba(0, 0, 0, 0);
}

[dir='ltr'] .swiper-nav-next {
  transform: rotate(-180deg);
}

[dir='rtl'] .swiper-nav-next {
  transform: rotate(0deg);
}

[dir='rtl'] .swiper-nav-prev {
  transform: rotate(-180deg);
}

@media (orientation: portrait) {
  #side {
    @apply h-10;
    z-index: 15;
  }

  #side .field {
    @apply rounded-3xl;
  }
}

@media screen and (orientation: landscape) {
  #side {
    height: 2.0625rem;
  }
}

@media screen and (orientation: landscape) and (min-width: 1024px) {
  #side {
    height: 2.28rem;
    margin-top: 0rem;
  }
}

.disabled-nav {
  filter: grayscale(100%);
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
